import { validateEnumTranslations } from '../../safety_checks';

//⚠️IMPACT attention changer dans le fichier prisma l'enum AvailabilityPrestationMode
export enum AvailabilityPrestationMode {
  ALL = 'ALL',
  SPECIFIC = 'SPECIFIC',
}

export const AvailabilityPrestationModeTranslation = {
  'fr-FR': {
    [`${AvailabilityPrestationMode.ALL}`]: 'Toutes les prestations',
    [`${AvailabilityPrestationMode.SPECIFIC}`]: 'Prestations spécifiques',
  },
};

// NB : l'enum AvailabilityRecurrence a été supprimé — la récurrence des disponibilités
// est portée par availability.recurrence_rrule (cf. lib/availability-recurrence-rule.ts),
// NULL = toujours actif (« chaque semaine »).

//⚠️IMPACT attention changer dans le fichier prisma l'enum BookingRecurrence et dans ce fichier
//⚠️IMPACT isRecurringBookingRecurrence
// L'enum est un RÉSUMÉ dérivé du FREQ de la règle RRULE (appointment.recurrence_rrule,
// cf. lib/recurrence-rule.ts) — il sert de discriminateur SQL (`!= 'ONCE'`, GROUP BY).
// Ordre des valeurs = ordre de l'enum Prisma/migrations (exigence verify-migrations).
export enum BookingRecurrence {
  ONCE = 'ONCE',
  EVERYDAY = 'EVERYDAY',
  EVERY_WEEK = 'EVERY_WEEK',
  EVERY_MONTH = 'EVERY_MONTH',
  EVERY_YEAR = 'EVERY_YEAR',
}

// Préfixe : évite toute collision de clés enum dans le spread plat de fr-FR.ts
// (ex. EVERY_WEEK, utilisé historiquement par d'autres namespaces).
export const TRANSLATE_KEY_BOOKING_RECURRENCE = 'BOOKING_RECURRENCE_';

export const BookingRecurrenceTranslation = {
  'fr-FR': {
    [`${TRANSLATE_KEY_BOOKING_RECURRENCE}${BookingRecurrence.ONCE}`]: 'Pas de récurrence',
    [`${TRANSLATE_KEY_BOOKING_RECURRENCE}${BookingRecurrence.EVERYDAY}`]: 'Chaque jour',
    [`${TRANSLATE_KEY_BOOKING_RECURRENCE}${BookingRecurrence.EVERY_WEEK}`]: 'Chaque semaine',
    [`${TRANSLATE_KEY_BOOKING_RECURRENCE}${BookingRecurrence.EVERY_MONTH}`]: 'Chaque mois',
    [`${TRANSLATE_KEY_BOOKING_RECURRENCE}${BookingRecurrence.EVERY_YEAR}`]: 'Chaque année',
  },
};

export type RecurringBookingRecurrence = Exclude<BookingRecurrence, BookingRecurrence.ONCE>;

export function isRecurringBookingRecurrence(recurrence: BookingRecurrence | string | null | undefined): boolean {
  return recurrence !== null && recurrence !== undefined && recurrence !== BookingRecurrence.ONCE;
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum RecurrenceUpdateMode (si ajouté en base)
export enum RecurrenceUpdateMode {
  THIS_ONLY = 'THIS_ONLY',
  THIS_AND_FOLLOWING = 'THIS_AND_FOLLOWING',
  ALL = 'ALL',
}

export const RecurrenceUpdateModeTranslation = {
  'fr-FR': {
    [`${RecurrenceUpdateMode.THIS_ONLY}`]: 'Ce rendez-vous uniquement',
    [`${RecurrenceUpdateMode.THIS_AND_FOLLOWING}`]: 'Ce rendez-vous et les suivants',
    [`${RecurrenceUpdateMode.ALL}`]: 'Tous les rendez-vous de la série',
  },
};

/**
 * Portée d'une annulation de réservation.
 *
 * `RESERVATION` (défaut) = comportement historique : toute la réservation part.
 * `APPOINTMENT` = annulation PARTIELLE d'un seul créneau d'une réservation qui en compte
 * plusieurs (chaîne de prestations) — la réservation reste active avec ses autres créneaux et son
 * total est recalculé. Exige `appointment_id`.
 */
export enum ReservationCancelScope {
  RESERVATION = 'RESERVATION',
  APPOINTMENT = 'APPOINTMENT',
  // Course multi-véhicules : retire UN véhicule (son aller ET son retour), les autres restent.
  // Une course ne s'annule jamais créneau par créneau (PARTIAL_CANCEL_RIDE_UNSUPPORTED).
  VEHICLE = 'VEHICLE',
}

export const ReservationCancelScopeTranslation = {
  'fr-FR': {
    [`${ReservationCancelScope.RESERVATION}`]: 'Toute la réservation',
    [`${ReservationCancelScope.APPOINTMENT}`]: 'Ce créneau uniquement',
    [`${ReservationCancelScope.VEHICLE}`]: 'Retirer ce véhicule (aller + retour)',
  },
};

//⚠️IMPACT attention changer dans le fichier prisma l'enum ReservationStatus
//⚠️IMPACT isCancelledReservation
export enum ReservationStatus {
  PENDING = 'PENDING',
  WAITING_CONFIRMATION = 'WAITING_CONFIRMATION',
  CONFIRMED = 'CONFIRMED',
  CANCELLED_BY_PRO = 'CANCELLED_BY_PRO',
  CANCELLED_BY_CUSTOMER = 'CANCELLED_BY_CUSTOMER',
}

export const ReservationStatusTranslation = {
  'fr-FR': {
    [`${ReservationStatus.PENDING}`]: 'En attente',
    [`${ReservationStatus.WAITING_CONFIRMATION}`]: 'En attente de confirmation',
    [`${ReservationStatus.CONFIRMED}`]: 'Confirmé',
    [`${ReservationStatus.CANCELLED_BY_PRO}`]: 'Annulé par le professionnel',
    [`${ReservationStatus.CANCELLED_BY_CUSTOMER}`]: 'Annulé par le client',
  },
};

export type ReservationLike =
  | {
      status?: ReservationStatus | string | null;
    }
  | null
  | undefined;

export function isCancelledReservation(reservation: ReservationLike): boolean {
  return (
    reservation?.status === ReservationStatus.CANCELLED_BY_PRO ||
    reservation?.status === ReservationStatus.CANCELLED_BY_CUSTOMER
  );
}

export function isWaitingConfirmationReservation(reservation: ReservationLike): boolean {
  return reservation?.status === ReservationStatus.WAITING_CONFIRMATION;
}

export type NoShowReservationLike =
  | {
      no_show_at?: string | Date | null;
    }
  | null
  | undefined;

/**
 * Client non présenté (« lapin ») : marquage porté par `reservation.no_show_at`. Ce n'est PAS un
 * statut — la réservation reste CONFIRMED (le créneau a été consommé), mais la visite n'est pas
 * honorée (statistiques, smart tags).
 */
export function isNoShowReservation(reservation: NoShowReservationLike): boolean {
  return Boolean(reservation?.no_show_at);
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum AppointmentType
export enum AppointmentType {
  INDIVIDUAL = 'INDIVIDUAL',
  GROUP = 'GROUP',
  RIDE = 'RIDE',
}

export const AppointmentTypeTranslation = {
  'fr-FR': {
    [`${AppointmentType.INDIVIDUAL}`]: 'Individuel',
    [`${AppointmentType.GROUP}`]: 'Groupe',
    [`${AppointmentType.RIDE}`]: 'Course',
  },
};

//⚠️IMPACT attention changer dans le fichier prisma l'enum RideTripLeg
export enum RideTripLeg {
  OUTBOUND = 'OUTBOUND',
  RETURN = 'RETURN',
}

export const RideTripLegTranslation = {
  'fr-FR': {
    [`${RideTripLeg.OUTBOUND}`]: 'Aller',
    [`${RideTripLeg.RETURN}`]: 'Retour',
  },
};

export enum RideTripType {
  ONE_WAY = 'ONE_WAY',
  ROUND_TRIP = 'ROUND_TRIP',
}

export const RideTripTypeTranslation = {
  'fr-FR': {
    [`${RideTripType.ONE_WAY}`]: 'Aller simple',
    [`${RideTripType.ROUND_TRIP}`]: 'Aller-retour',
  },
};

//⚠️IMPACT attention changer dans le fichier prisma l'enum RidePricingPreference
export enum RidePricingPreference {
  SHORTEST_TIME = 'SHORTEST_TIME',
  SHORTEST_DISTANCE = 'SHORTEST_DISTANCE',
  LONGEST_TIME = 'LONGEST_TIME',
  LONGEST_DISTANCE = 'LONGEST_DISTANCE',
}

export const TRANSLATE_KEY_RIDE_PRICING_PREFERENCE = 'RIDE_PRICING_PREFERENCE_';

export const RidePricingPreferenceTranslation = {
  'fr-FR': {
    [`${TRANSLATE_KEY_RIDE_PRICING_PREFERENCE}${RidePricingPreference.SHORTEST_TIME}`]: 'Temps le plus court',
    [`${TRANSLATE_KEY_RIDE_PRICING_PREFERENCE}${RidePricingPreference.SHORTEST_DISTANCE}`]: 'Distance la plus courte',
    [`${TRANSLATE_KEY_RIDE_PRICING_PREFERENCE}${RidePricingPreference.LONGEST_TIME}`]: 'Temps le plus long',
    [`${TRANSLATE_KEY_RIDE_PRICING_PREFERENCE}${RidePricingPreference.LONGEST_DISTANCE}`]: 'Distance la plus longue',
  },
};

// Tarif dégressif d'un véhicule (pas un enum Prisma : encodé dans pro_resource_pricing_tier.
// distance_bands — null = INHERIT, [] = NONE, liste = CUSTOM, cf. ride-distance-bands.ts).
export enum RideDistanceBandsMode {
  INHERIT = 'INHERIT',
  NONE = 'NONE',
  CUSTOM = 'CUSTOM',
}

export const TRANSLATE_KEY_RIDE_DISTANCE_BANDS_MODE = 'RIDE_DISTANCE_BANDS_MODE_';

export const RideDistanceBandsModeTranslation = {
  'fr-FR': {
    [`${TRANSLATE_KEY_RIDE_DISTANCE_BANDS_MODE}${RideDistanceBandsMode.INHERIT}`]: 'Grille de la config course',
    [`${TRANSLATE_KEY_RIDE_DISTANCE_BANDS_MODE}${RideDistanceBandsMode.NONE}`]: 'Aucun dégressif',
    [`${TRANSLATE_KEY_RIDE_DISTANCE_BANDS_MODE}${RideDistanceBandsMode.CUSTOM}`]: 'Grille propre à ce véhicule',
  },
};

export enum AgendaViewEnum {
  DAY = 'day',
  THREE_DAYS = '3days',
  WEEK = 'week',
  MONTH = 'month',
  PLANNING = 'planning',
}

/** Ordering of the agenda lanes: team members first or resources first. */
export enum AgendaPivotEnum {
  MEMBERS_FIRST = 'members-first',
  RESOURCES_FIRST = 'resources-first',
}

export const DAYS_PER_VIEW: Record<AgendaViewEnum, number> = {
  [AgendaViewEnum.DAY]: 1,
  [AgendaViewEnum.THREE_DAYS]: 3,
  [AgendaViewEnum.WEEK]: 7,
  [AgendaViewEnum.MONTH]: 31,
  [AgendaViewEnum.PLANNING]: 45,
};

export const AgendaViewEnumTranslation = {
  'fr-FR': {
    [`${AgendaViewEnum.DAY}`]: 'Jour',
    [`${AgendaViewEnum.THREE_DAYS}`]: '3 jours',
    [`${AgendaViewEnum.WEEK}`]: 'Semaine',
    [`${AgendaViewEnum.MONTH}`]: 'Mois',
    [`${AgendaViewEnum.PLANNING}`]: 'Planning',
  },
};

export enum AgendaGroupByEnum {
  TEAM_MEMBER = 'team_member',
  RESOURCE = 'resource',
  SELF_SERVICE_PRESTATION = 'self_service_prestation',
}

export const AgendaGroupByEnumTranslation = {
  'fr-FR': {
    [`${AgendaGroupByEnum.TEAM_MEMBER}`]: 'Par membre',
    [`${AgendaGroupByEnum.RESOURCE}`]: 'Par ressource',
    [`${AgendaGroupByEnum.SELF_SERVICE_PRESTATION}`]: 'Par prestation libre-service',
  },
};

//DONT TOUCH below this line
validateEnumTranslations(AvailabilityPrestationMode, AvailabilityPrestationModeTranslation, undefined, true);
validateEnumTranslations(BookingRecurrence, BookingRecurrenceTranslation, TRANSLATE_KEY_BOOKING_RECURRENCE, true);
validateEnumTranslations(RecurrenceUpdateMode, RecurrenceUpdateModeTranslation, undefined, true);
validateEnumTranslations(ReservationCancelScope, ReservationCancelScopeTranslation, undefined, true);
validateEnumTranslations(ReservationStatus, ReservationStatusTranslation, undefined, true);
validateEnumTranslations(AppointmentType, AppointmentTypeTranslation, undefined, true);
validateEnumTranslations(RideTripLeg, RideTripLegTranslation, undefined, true);
validateEnumTranslations(RideTripType, RideTripTypeTranslation, undefined, true);
validateEnumTranslations(
  RidePricingPreference,
  RidePricingPreferenceTranslation,
  TRANSLATE_KEY_RIDE_PRICING_PREFERENCE,
  true,
);
validateEnumTranslations(
  RideDistanceBandsMode,
  RideDistanceBandsModeTranslation,
  TRANSLATE_KEY_RIDE_DISTANCE_BANDS_MODE,
  true,
);
validateEnumTranslations(AgendaViewEnum, AgendaViewEnumTranslation, undefined, true);
validateEnumTranslations(AgendaGroupByEnum, AgendaGroupByEnumTranslation, undefined, true);
